import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/property';
import { persistSignupSourceForUser } from '@/lib/signupSource';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  console.log('[useAuth] Hook called, isLoading:', isLoading);

  useEffect(() => {
    let isMounted = true;
    let profileFetched = false;

    // Get initial session first
    console.log('[useAuth] Getting initial session...');
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      console.log('[useAuth] Initial session retrieved:', session ? 'exists' : 'null');
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user && !profileFetched) {
        profileFetched = true;
        fetchProfile(session.user.id);
        void persistSignupSourceForUser(session.user.id);
      }
      console.log('[useAuth] Setting isLoading to false');
      setIsLoading(false);
    }).catch((error) => {
      console.error('[useAuth] Error getting session:', error);
      setIsLoading(false);
    });

    // Listen for auth changes (sign in/out)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      
      if (event === 'SIGNED_IN' && session?.user && !profileFetched) {
        profileFetched = true;
        fetchProfile(session.user.id);
        void persistSignupSourceForUser(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        setProfile(null);
        profileFetched = false;
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (!error && data) {
        // Cast the database response to UserProfile type
        setProfile(data as unknown as UserProfile);
      } else if (error) {
        console.error('Error fetching profile:', error);
      }
    } catch (err) {
      console.error('Unexpected error fetching profile:', err);
    }
  };

  const signOut = async () => {
    // Clear local state first
    setUser(null);
    setSession(null);
    setProfile(null);
    
    try {
      // Attempt server-side signout, but don't fail if session is already invalid
      await supabase.auth.signOut({ scope: 'local' });
    } catch (error) {
      console.log('SignOut error (ignored):', error);
    }
    
    // Force clear any remaining auth data from localStorage
    localStorage.removeItem('sb-rnxqjbjjfkufnyisucli-auth-token');
  };

  return { user, session, profile, isLoading, signOut, refetchProfile: () => user && fetchProfile(user.id) };
}
